import { Button, Container } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Helmet } from 'react-helmet';
import Adsense from '../Ads/Adsense';
import { Card } from '../Card/Card';
import CompoundChart from './Chart/CompoundChart';
import CompoundPieChart from './Chart/CompoundPieChart';
import { calculateCompound, CalculatorOptions, formatNumber } from './CompoundPage.hooks';
import styles from './CompoundPage.module.css';
import { CompoundForm } from './Form/CompoundForm';
import CompoundTable from './Table/CompoundTable';

function CompoundPage() {
  const navigate = useNavigate();
  const [optionsHistories, setOptionsOptionsHistories] = useState<CalculatorOptions[]>([]);
  const [currentOptions, setCurrentOptions] = useState<CalculatorOptions>();
  const resultElementRef = useRef<HTMLDivElement>(null);

  const handleSubmit = useCallback(
    (options: CalculatorOptions) => {
      const sameOptionsIndex = optionsHistories.findIndex((each) => {
        return (
          each.initialAmount === options.initialAmount &&
          each.investFrequency === options.investFrequency &&
          each.frequentInvestment === options.frequentInvestment &&
          each.period === options.period &&
          each.interestPercentage === options.interestPercentage
        );
      });

      if (sameOptionsIndex === -1) {
        setOptionsOptionsHistories([...optionsHistories, options]);
      } else {
        const newOptionsHistories = [...optionsHistories];
        newOptionsHistories.splice(sameOptionsIndex, 1);
        setOptionsOptionsHistories([...newOptionsHistories, options]);
      }
      setCurrentOptions(options);

      setTimeout(() => {
        resultElementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    },
    [optionsHistories]
  );

  const data = useMemo(() => {
    if (!currentOptions) return undefined;
    return calculateCompound(currentOptions);
  }, [currentOptions]);

  return (
    <div className={styles.root}>
      <Helmet>
        <title>복리 계산기 - 투자의 미래 가치를 확인하세요</title>
        <meta
          name="description"
          content="초기 투자금, 매월 투자금, 투자 기간, 연 수익률을 입력하여 복리 효과를 시뮬레이션하고, 미래의 자산 가치를 그래프와 표로 확인해보세요."
        />
      </Helmet>
      <Container maxWidth="sm">
        <Card className={styles.formCard}>
          <div className={styles.header}>
            <h1 className={styles.title}>복리 계산기</h1>
            <p className={styles.description}>투자의 미래 가치를 계산해보세요.</p>
          </div>
          <CompoundForm className={styles.form} onSubmit={handleSubmit} />
        </Card>
      </Container>
      <Adsense className={styles.adsense} adClient="ca-pub-9359050149154028" adSlot="3275566807" />
      {data && currentOptions && (() => {
        const lastHistory = data.yearlyHistories[data.yearlyHistories.length - 1];
        const profitRate = lastHistory.principalAmount > 0
          ? ((lastHistory.amount - lastHistory.principalAmount) / lastHistory.principalAmount * 100).toFixed(1)
          : '0';

        const handleRetirementSimulation = () => {
          navigate(`/retirement?asset=${Math.round(lastHistory.amount)}`);
        };

        return (
          <div>
            <Container maxWidth="xl">
              <Card className={styles.resultCard} ref={resultElementRef}>
                <h2 className={styles.resultCardTitle}>
                  계산 결과
                  <span className={styles.profitBadge}>+{profitRate}% 수익</span>
                  <small>
                    {currentOptions.period}년 후,{' '}
                    <strong>
                      {formatNumber(lastHistory.amount)}원
                    </strong>
                    을 모으게 됩니다.
                  </small>
                </h2>
                <div className={styles.resultCardBody}>
                  <div className={styles.chartSection}>
                    <div className={styles.areaChartWrapper}>
                      <CompoundChart className={styles.chart} histories={data.yearlyHistories} />
                    </div>
                    <div className={styles.pieChartWrapper}>
                      <h3 className={styles.pieChartTitle}>원금 vs 이자 수익 비율</h3>
                      <CompoundPieChart lastHistory={lastHistory} />
                    </div>
                  </div>
                  <CompoundTable className={styles.table} histories={data.yearlyHistories} />
                </div>
                <div className={styles.actionButtons}>
                  <Button variant="outlined" onClick={handleRetirementSimulation}>
                    이 금액으로 은퇴 시뮬레이션 하기
                  </Button>
                </div>
              </Card>
            </Container>
            <Adsense
              className={styles.adsense}
              adClient="ca-pub-9359050149154028"
              adSlot="9320394113"
            />
          </div>
        );
      })()}
    </div>
  );
}

export default CompoundPage;
